<template>
  <ClaimComplementRequestReview
    v-if="reviewing"
    :requests="requests"
    :submitting="submitting"
    @edit="reviewing = false"
    @confirm="send"
  />

  <div v-else>
    <p class="mb-2 text-xs font-bold uppercase tracking-wider text-gray-500" data-test="requests-count">
      {{ t("requests_label", requests.length) }}
    </p>
    <div class="flex flex-col gap-2.5">
      <ClaimComplementRequestItem
        v-for="(request, index) in requests"
        :key="request.field"
        :field="request.field"
        :message="request.message"
        :suggested="request.suggested"
        :declaration="declaration"
        @update:message="request.message = $event"
        @remove="remove(index)"
      />
      <p
        v-if="!requests.length"
        class="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500"
        data-test="empty"
      >
        {{ t("empty") }}
      </p>
    </div>

    <button
      type="button"
      class="mt-3 w-full rounded-lg border border-dashed border-primary px-4 py-2 text-sm font-semibold text-primary-dark hover:bg-primary-light"
      data-test="toggle-palette"
      @click="paletteOpen = !paletteOpen"
    >
      {{ paletteOpen ? t("close_palette") : t("add_request") }}
    </button>
    <ClaimComplementFieldPalette
      v-if="paletteOpen"
      class="mt-2"
      :declaration="declaration"
      :used-fields="requests.map((request) => request.field)"
      :suggested-fields="Object.keys(agentMessages)"
      @add="add"
    />

    <div class="mt-5 flex justify-end">
      <GenericButton
        default-style="bg-yellow-400 hover:bg-yellow-500 border-transparent text-yellow-900"
        :disabled="!requests.length"
        data-test="review-request"
        @click="reviewing = true"
      >
        {{ t("review_button", requests.length) }}
      </GenericButton>
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { mapActions } from "vuex";
import { GenericButton } from "@estaly/ui";
import ClaimComplementFieldPalette from "./ClaimComplementFieldPalette.vue";
import ClaimComplementRequestItem from "./ClaimComplementRequestItem.vue";
import ClaimComplementRequestReview from "./ClaimComplementRequestReview.vue";
import { FIELD_GROUPS, withPairedFields } from "./complementFields";

const KNOWN_FIELDS = new Set(FIELD_GROUPS.flatMap((group) => group.fields));

export default {
  name: "ClaimComplementRequestForm",

  components: {
    GenericButton,
    ClaimComplementFieldPalette,
    ClaimComplementRequestItem,
    ClaimComplementRequestReview,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  props: {
    // Déclaration à compléter : ses réponses actuelles s'affichent à côté de chaque demande.
    declaration: {
      type: Object,
      required: true,
    },
    // Demande proposée par l'agent général ({ clientMessage, requests }), quand c'est son avis.
    agentComplement: {
      type: Object,
      default: null,
    },
  },

  emits: ["success"],

  data() {
    // Un champ sans message est ajouté par l'agent pour son jumelage côté API : ce n'est pas
    // une question pour le client, il revient d'office à l'envoi (withPairedFields).
    const agentRequests = (this.agentComplement?.requests || []).filter(
      (request) => request.clientMessage && KNOWN_FIELDS.has(request.field),
    );
    return {
      requests: agentRequests.map((request) => ({
        field: request.field,
        message: request.clientMessage,
        suggested: true,
      })),
      // Messages de l'agent par champ : une proposition retirée se rajoute à l'identique.
      agentMessages: Object.fromEntries(agentRequests.map((request) => [request.field, request.clientMessage])),
      paletteOpen: false,
      reviewing: false,
      submitting: false,
    };
  },

  methods: {
    ...mapActions("claims", ["requestAdditionalInfo"]),
    ...mapActions("notifications", ["notify"]),

    add(field) {
      const suggested = field in this.agentMessages;
      this.requests.push({ field, message: suggested ? this.agentMessages[field] : "", suggested });
      this.paletteOpen = false;
    },
    remove(index) {
      this.requests.splice(index, 1);
    },

    async send() {
      this.submitting = true;
      const response = await this.requestAdditionalInfo({
        declarationId: this.declaration.id,
        fields: withPairedFields(this.requests.map((request) => request.field)),
        fieldMessages: this.requests
          .filter((request) => request.message.trim())
          .map((request) => ({ field: request.field, message: request.message.trim() })),
      });
      this.submitting = false;
      if (!response?.success) return;

      await this.notify({ category: "simple", type: "success", title: this.t("submit_success") });
      this.$emit("success");
    },
  },
};
</script>

<i18n src="./ClaimComplementRequestForm.i18n.json"></i18n>
